import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Skeleton,
    Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";

const PromotionCardSkeleton = () => (
    <Paper
        elevation={1}
        sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: '8px',
            border: '1px solid #E0E0E0',
            height: '100%'
        }}
    >
        <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="90%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="50%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
            <Skeleton variant="text" width="80%" height={16} />
            <Skeleton variant="text" width="75%" height={16} />
            <Skeleton variant="text" width="70%" height={16} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width="48%" height={32} />
            <Skeleton variant="rounded" width="48%" height={32} />
        </Box>
    </Paper>
);

const PromotionCardSkeletonWithBanner = () => (
    <Paper
        elevation={1}
        sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: '8px',
            border: '1px solid #E0E0E0',
            position: 'relative',
            height: '100%'
        }}
    >
        <Box
            sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: { xs: '35px', sm: '40px' },
                backgroundColor: '#ffd6dd',
                borderRadius: '8px',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px'
            }}
        />
        <Box sx={{ pr: { xs: 3, sm: 4 } }}>
            <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="90%" height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="50%" height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                <Skeleton variant="text" width="80%" height={16} />
                <Skeleton variant="text" width="75%" height={16} />
                <Skeleton variant="text" width="70%" height={16} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="rounded" width="48%" height={32} />
                <Skeleton variant="rounded" width="48%" height={32} />
            </Box>
        </Box>
    </Paper>
);

const PromotionsDialog = ({
    open,
    onClose,
    appliedPromotions = [],
    availablePromotions = [],
    isLoading = false,
    isActionPending = false,
    totalApplicable = 0,
    onOpenDrawer,
    onRemovePromotion,
    onApplyPromotion
}) => {
    const showRemovePromotion = Number(totalApplicable) > 1;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxHeight: '80vh',
                    m: { xs: 1, sm: 1 }
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    px: 3,
                    py: 1.5,
                    mb: 2,
                    borderBottom: '1px dashed #E0E0E0'
                }}
            >
                Promotions
                <IconButton onClick={onClose} sx={{ color: 'text.secondary' }} size="small">
                    <Iconify icon="mdi:close" width={24} height={24} />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 1.5, sm: 3 }, minHeight: '400px' }}>
                <Box sx={{ mb: { xs: 2, sm: 3 }, minHeight: '200px' }}>
                    <Typography fontWeight={700} sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Applied Promotions
                    </Typography>
                    {isLoading ? (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {[1, 2].map((item) => (
                                <Grid size={{ xs: 12, md: 6 }} key={item}>
                                    <PromotionCardSkeleton />
                                </Grid>
                            ))}
                        </Grid>
                    ) : appliedPromotions.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No promotions applied
                        </Typography>
                    ) : (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {appliedPromotions.map((promo) => (
                                <Grid size={{ xs: 12, md: 6 }} key={promo.id}>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            borderRadius: '8px',
                                            border: '1px solid #E0E0E0',
                                            position: 'relative',
                                            height: '100%'
                                        }}
                                    >
                                        <Box sx={{ pr: { xs: 3, sm: 4 } }}>
                                            <Typography fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                                                {promo.title}
                                            </Typography>
                                            <Typography sx={{ mb: 1, display: 'block', fontSize: '0.7rem' }}>
                                                <span style={{ fontWeight: 600 }}>Validity:</span> {promo.validity}
                                            </Typography>
                                            {promo.products && promo.products.length > 0 && (
                                                <>
                                                    <Typography sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                                        <span style={{ fontWeight: 600 }}>Included Products:</span> ({promo.products.length})
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2, backgroundColor: '#fcfcfc', p: 1, borderRadius: '8px' }}>
                                                        {promo.products.slice(0, 3).map((product, idx) => (
                                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Iconify icon="mdi:check-circle" width={16} height={16} sx={{ color: '#4CAF50' }} />
                                                                <Typography fontSize="0.7rem">{product}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </>
                                            )}
                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.75, sm: 1 }, mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    fullWidth
                                                    onClick={() => onOpenDrawer(promo)}
                                                    sx={{
                                                        backgroundColor: '#424242',
                                                        color: '#fff',
                                                        textTransform: 'none',
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        px: 1.5,
                                                        py: { xs: 0.75, sm: 0.5 },
                                                        fontWeight: 500,
                                                        '&:hover': { backgroundColor: '#616161' }
                                                    }}
                                                >
                                                    View More
                                                </Button>
                                                {showRemovePromotion ? (
                                                    <Button
                                                        variant="soft"
                                                        color="error"
                                                        size="small"
                                                        fullWidth
                                                        disabled={isActionPending}
                                                        startIcon={<Iconify icon="mdi:delete" width={16} height={16} />}
                                                        onClick={() => onRemovePromotion(promo)}
                                                        sx={{
                                                            fontWeight: 500,
                                                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                            px: 1.5,
                                                            py: { xs: 0.75, sm: 0.5 }
                                                        }}
                                                    >
                                                        {isActionPending ? 'Removing...' : 'Remove Promotion'}
                                                    </Button>
                                                ) : null}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
                <Box sx={{ minHeight: '200px' }}>
                    <Typography fontWeight={700} sx={{ mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Available Promotions
                    </Typography>
                    {isLoading ? (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {[1, 2].map((item) => (
                                <Grid size={{ xs: 12, md: 6 }} key={item}>
                                    <PromotionCardSkeletonWithBanner />
                                </Grid>
                            ))}
                        </Grid>
                    ) : availablePromotions.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            No available promotions
                        </Typography>
                    ) : (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            {availablePromotions.map((promo) => (
                                <Grid size={{ xs: 12, md: 6 }} key={promo.id}>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            borderRadius: '8px',
                                            border: '1px solid #E0E0E0',
                                            position: 'relative',
                                            height: '100%'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                right: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: { xs: '35px', sm: '40px' },
                                                backgroundColor: '#ffd6dd',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderTopRightRadius: '8px',
                                                borderBottomRightRadius: '8px'
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#f1031ab0',
                                                    fontWeight: 700,
                                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                                    transform: 'rotate(90deg)',
                                                    whiteSpace: 'nowrap',
                                                    letterSpacing: '0.2px'
                                                }}
                                            >
                                                {promo.banner}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ pr: { xs: 3, sm: 4 } }}>
                                            <Typography fontWeight={600} sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                                                {promo.title}
                                            </Typography>
                                            <Typography sx={{ mb: 1, display: 'block', fontSize: '0.7rem' }}>
                                                <span style={{ fontWeight: 600 }}>Validity:</span> {promo.validity}
                                            </Typography>
                                            <Typography sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                                <span style={{ fontWeight: 600 }}>Included Products:</span> ({promo.products?.length ?? 0})
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2, backgroundColor: '#fcfcfc', p: 1, borderRadius: '8px' }}>
                                                {(promo.products || []).slice(0, 3).map((product, idx) => (
                                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Iconify icon="mdi:check-circle" width={16} height={16} sx={{ color: '#4CAF50' }} />
                                                        <Typography variant="caption">{product}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.75, sm: 1 }, mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    fullWidth
                                                    onClick={() => onOpenDrawer(promo)}
                                                    sx={{
                                                        backgroundColor: '#424242',
                                                        color: '#fff',
                                                        textTransform: 'none',
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        px: 1.5,
                                                        py: { xs: 0.75, sm: 0.5 },
                                                        fontWeight: 500,
                                                        '&:hover': { backgroundColor: '#616161' }
                                                    }}
                                                >
                                                    View More
                                                </Button>
                                                <Button
                                                    variant="soft"
                                                    size="small"
                                                    fullWidth
                                                    disabled={isActionPending}
                                                    startIcon={<Iconify icon="mdi:lightning-bolt" width={16} height={16} />}
                                                    onClick={() => onApplyPromotion(promo)}
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        px: 1.5,
                                                        py: { xs: 0.75, sm: 0.5 },
                                                        backgroundColor: '#E8F5E9',
                                                        color: '#4CAF50',
                                                        '&:hover': { backgroundColor: '#C8E6C9' }
                                                    }}
                                                >
                                                    {isActionPending ? 'Applying...' : 'Apply Promotion'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 1, sm: 2 }, mt: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        className="button-variant-outlined"
                        onClick={onClose}
                        
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        className="button-variant-contained"
                    >
                        Close
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PromotionsDialog;
